// Helper functions for project selectors

import type {
  EnrichedProject,
  LightProject,
  Project,
  ProjectBase,
  ProjectVariant,
  ProjectVariantMap,
} from 'types/project';
import { enrichTicMeta } from 'utils/tic/enrichTicMeta';
import { makeFirstArgumentOptional } from 'utils/typesafe';

export function extractProjectBase(variant: ProjectVariant): ProjectBase {
  return variant.type === 'full' ? variant.project : variant.lightProject;
}

export function projectSelectorFilterProjectIds(
  permissions: Array<number>,
  projectMap: ProjectVariantMap,
  maxProjectCount = 10
) {
  return permissions
    .filter(projectId => {
      const variant = projectMap[projectId];
      if (!variant) {
        return false;
      }
      const project = extractProjectBase(variant);
      const isProjectUnavailable = project == null || !project.active;
      return !isProjectUnavailable;
    })
    .slice(0, maxProjectCount);
}

export function enrichProjectVariant(
  variant: ProjectVariant,
  pinnedProjectIds: Set<number>
): EnrichedProject {
  const base = enrichTicMeta(extractProjectBase(variant));
  const fullProject = variant.type === 'full' ? variant.project : null;
  return {
    base,
    fullProject,
    isFullyLoaded: fullProject != null,
    isPinned: pinnedProjectIds.has(base.id),
  };
}

export function enrichLightProject(
  project: LightProject,
  pinnedProjectIds?: Set<number>
): EnrichedProject {
  return enrichProjectVariant(
    { type: 'light', lightProject: project },
    pinnedProjectIds ?? new Set()
  );
}

export const enrichProjectVariantOrNull = makeFirstArgumentOptional(enrichProjectVariant);

export function getFullProjectOrNull(variant: ProjectVariant | null | undefined): Project | null {
  return variant?.type === 'full' ? variant.project : null;
}
